import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  fetchOneMinutePaperCompliance,
  fetchOneMinutePaperComplianceReservas,
  OneMinutePaperComplianceProfesorRow,
} from '../../../api/oneMinutePaper';

const CONTROL_START_DATE = '2026-04-13';

function getDefaultHasta() {
  const hoy = dayjs().format('YYYY-MM-DD');
  return hoy < CONTROL_START_DATE ? CONTROL_START_DATE : hoy;
}

export default function FirmasIncumplimientoPage() {
  const [desde, setDesde] = useState(CONTROL_START_DATE);
  const [hasta, setHasta] = useState(getDefaultHasta());
  const [search, setSearch] = useState('');

  const [submitted, setSubmitted] = useState({
    desde,
    hasta,
    search: '',
  });

  const [profesorSel, setProfesorSel] = useState<OneMinutePaperComplianceProfesorRow | null>(null);

  const rangoValido = useMemo(
    () => dayjs(desde).isValid() && dayjs(hasta).isValid() && !dayjs(desde).isAfter(hasta),
    [desde, hasta],
  );

  const rankingQuery = useQuery({
    queryKey: ['one-minute-paper-compliance-profesores', submitted],
    queryFn: () => fetchOneMinutePaperCompliance(submitted),
  });

  const detalleQuery = useQuery({
    queryKey: [
      'one-minute-paper-compliance-detalle-profesor',
      profesorSel?.profesorId,
      submitted.desde,
      submitted.hasta,
    ],
    queryFn: () =>
      fetchOneMinutePaperComplianceReservas(profesorSel!.profesorId, {
        desde: submitted.desde,
        hasta: submitted.hasta,
      }),
    enabled: Boolean(profesorSel),
  });

  const onBuscar = () => {
    if (!rangoValido) return;
    setSubmitted({
      desde: desde < CONTROL_START_DATE ? CONTROL_START_DATE : desde,
      hasta,
      search: search.trim(),
    });
  };

  const getSeverityColor = (pct: number) => {
    if (pct > 25) return 'error';
    if (pct > 0) return 'warning';
    return 'success';
  };

  const rows = rankingQuery.data?.rows ?? [];

  return (
    <Stack spacing={2}>
      <Typography variant="h5">
        Incumplimiento de ejercicio de inicio de clase por profesor
      </Typography>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="date"
            label="Desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="Hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            error={!rangoValido}
            helperText={!rangoValido ? 'Rango inválido' : ''}
          />
          <TextField
            label="Profesor"
            placeholder="Nombre opcional"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
          />
          <Button variant="contained" onClick={onBuscar} disabled={!rangoValido}>
            Buscar
          </Button>
        </Stack>
      </Paper>

      <Alert severity="info">
        El control de incumplimiento de ejercicio de inicio de clase se calcula desde el 13/04/2026
        y genera alerta cuando el incumplimiento supera el 25% de las reservas aplicables.
      </Alert>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Profesores detectados
          </Typography>
          <Chip
            size="small"
            label={`Profesores: ${rankingQuery.data?.totalProfesores ?? 0}`}
            variant="outlined"
          />
          <Chip
            size="small"
            color="error"
            label={`En alerta (>25%): ${rankingQuery.data?.totalProfesoresEnAlerta ?? 0}`}
          />
          <Chip
            size="small"
            color="warning"
            label={`Reservas sin evaluación: ${rankingQuery.data?.reservasSinEvaluacion ?? 0}`}
          />
        </Stack>

        {rankingQuery.isLoading && (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {rankingQuery.isError && (
          <Alert severity="error">
            No se pudo cargar el informe de incumplimiento de ejercicio de inicio de clase.
          </Alert>
        )}

        {!rankingQuery.isLoading && !rankingQuery.isError && rows.length === 0 && (
          <Alert severity="info">
            No hay profesores con reservas aplicables sin evaluación en el rango seleccionado.
          </Alert>
        )}

        {!rankingQuery.isLoading && !rankingQuery.isError && rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Profesor</TableCell>
                <TableCell align="right">Sin evaluación</TableCell>
                <TableCell align="right">Total aplicables</TableCell>
                <TableCell align="right">% incumplimiento</TableCell>
                <TableCell>Última pendiente</TableCell>
                <TableCell align="center">Alerta</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.profesorId} hover>
                  <TableCell>{row.profesor}</TableCell>
                  <TableCell align="right">{row.reservasSinEvaluacion}</TableCell>
                  <TableCell align="right">{row.totalReservasAplicables}</TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      color={getSeverityColor(row.porcentajeIncumplimiento)}
                      label={`${row.porcentajeIncumplimiento.toFixed(2)}%`}
                    />
                  </TableCell>
                  <TableCell>{row.ultimaPendiente ?? '—'}</TableCell>
                  <TableCell align="center">
                    {row.enAlerta ? (
                      <Chip size="small" color="error" label="Sí" />
                    ) : (
                      <Chip size="small" color="success" label="No" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      onClick={() => setProfesorSel(row)}
                    >
                      Ver detalle
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog
        open={Boolean(profesorSel)}
        onClose={() => setProfesorSel(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {profesorSel
            ? `Reservas sin evaluación de ejercicio de inicio de clase de ${profesorSel.profesor}`
            : 'Reservas sin evaluación de ejercicio de inicio de clase'}
        </DialogTitle>
        <DialogContent dividers>
          {detalleQuery.isLoading && (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {detalleQuery.isError && (
            <Alert severity="error">No se pudo cargar el detalle del profesor.</Alert>
          )}

          {!detalleQuery.isLoading && !detalleQuery.isError && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Horario</TableCell>
                  <TableCell>Asignatura</TableCell>
                  <TableCell>Centro</TableCell>
                  <TableCell align="right">Evaluaciones</TableCell>
                  <TableCell>Alumnos pendientes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detalleQuery.data ?? []).map((row) => (
                  <TableRow key={row.reservaId}>
                    <TableCell>{row.fecha}</TableCell>
                    <TableCell>{`${row.horaInicio} - ${row.horaFin}`}</TableCell>
                    <TableCell>{row.asignatura}</TableCell>
                    <TableCell>{row.centro}</TableCell>
                    <TableCell align="right">
                      {row.evaluacionesRegistradas}/{row.alumnosEsperados}
                    </TableCell>
                    <TableCell>{row.alumnosPendientes || '—'}</TableCell>
                  </TableRow>
                ))}
                {!detalleQuery.data?.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Alert severity="info">
                        No hay reservas sin evaluación de ejercicio de inicio de clase para este
                        profesor en el rango.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
